/**
 * Permission Registry - Single Source of Truth for All Permissions
 *
 * This file defines all available permissions in the system.
 * Permissions are used to control access to features and resources.
 *
 * Permission Naming Convention: <action>_<resource>
 * Examples: manage_users, invite_users
 *
 * Permission Types:
 * - High-level (manage_X): Full CRUD + special operations
 * - Granular (create_X, read_X, update_X, delete_X): Specific actions
 *
 * Important: Routes should accept BOTH high-level OR granular permissions
 * using requireAnyPermission([PERMISSIONS.MANAGE_USERS, PERMISSIONS.INVITE_USERS])
 */

export const PERMISSIONS = {
  // ===== USER MANAGEMENT =====
  MANAGE_USERS: 'manage_users', // Full access to all user operations
  INVITE_USERS: 'invite_users', // Send invitations to new members
  REMOVE_USERS: 'remove_users', // Remove members from organization
  EDIT_USER_ROLES: 'edit_user_roles', // Change member roles and permissions
  VIEW_USERS: 'view_users', // View member list

  // ===== ROLE & PERMISSION MANAGEMENT =====
  MANAGE_ROLES: 'manage_roles', // Create/edit/delete roles and permissions

  // ===== ATTACHMENTS =====
  MANAGE_ATTACHMENTS: 'manage_attachments', // Full attachment management (upload, view, delete)

  // ===== ORGANIZATION KNOWLEDGE =====
  MANAGE_KNOWLEDGE: 'manage_knowledge', // Full knowledge base management (upload, view, delete)

  // ===== ORGANIZATION SETTINGS =====
  MANAGE_ORGANIZATION_SETTINGS: 'manage_organization_settings', // Manage organization profile and settings

  // ===== ROROS — Rocket Routine OS =====
  ROROS_DOMAINS_VIEW: 'roros.domains.view',
  ROROS_DOMAINS_MANAGE: 'roros.domains.manage',
  ROROS_ARTIFACTS_CREATE: 'roros.artifacts.create',
  ROROS_ARTIFACTS_EDIT: 'roros.artifacts.edit',
  ROROS_ARTIFACTS_DELETE: 'roros.artifacts.delete',
  ROROS_ACTORS_MANAGE: 'roros.actors.manage',
  ROROS_ADOPTION_LEVEL_CHANGE: 'roros.adoption-level.change',
  ROROS_ROLE_CONTRACTS_MANAGE: 'roros.role-contracts.manage',
  ROROS_CONTROL_TOWER_MANAGE: 'roros.control-tower.manage',
  ROROS_CONTROL_TOWER_VIEW: 'roros.control-tower.view',
  // v1.1.19 — destructive hard-delete of work items (with cascade). Owner+Admin
  // only. Removes the row + all sub-items + history + links + qc reports
  // permanently. Distinct from MANAGE so it can be revoked separately if needed.
  ROROS_CONTROL_TOWER_DELETE: 'roros.control-tower.delete',
  ROROS_WORK_ITEMS_TRANSITION: 'roros.work-items.transition',
  ROROS_DECISIONS_CREATE: 'roros.decisions.create',
  ROROS_DECISIONS_APPROVE: 'roros.decisions.approve',
  ROROS_QUALITY_CREATE: 'roros.quality.create',
  ROROS_GOVERNANCE_MANAGE: 'roros.governance.manage',
  ROROS_GOVERNANCE_VIEW: 'roros.governance.view',
  ROROS_SETTINGS_MANAGE: 'roros.settings.manage',
  ROROS_STRUCTURE_MANAGE: 'roros.structure.manage',
  ROROS_OVERARCHING_MANAGE: 'roros.overarching.manage',
  ROROS_OVERARCHING_VIEW: 'roros.overarching.view',
  ROROS_CADENCE_MANAGE: 'roros.cadence.manage',
  ROROS_OPERATING_MODE_CHANGE: 'roros.operating-mode.change',
  ROROS_AGENT_SKILLS_MANAGE: 'roros.agent-skills.manage',
  ROROS_AGENT_SKILLS_VIEW: 'roros.agent-skills.view',
  ROROS_OBSERVABILITY_VIEW: 'roros.observability.view',
  /** Budget Ledger — Owner + Admin can view per-operator spend (CEO-level insight). */
  ROROS_BUDGET_LEDGER_VIEW: 'roros.budget-ledger.view',
  /** Delegation proxy-create — Owner only. Allows creating a delegation on behalf of another user's actor. */
  ROROS_DELEGATION_PROXY_CREATE: 'roros.delegation.proxy-create',

  // ─── M-Rhythm-1 Meetings permissions ──────────────────────────────────────
  /** Meetings read — Owner, Admin, Member, Employee. View meetings and agenda. */
  ROROS_MEETINGS_READ: 'roros.meetings.read',
  /** Meetings write — Owner, Admin. Create, edit, skip, delete ad-hoc meetings. */
  ROROS_MEETINGS_WRITE: 'roros.meetings.write',
  /**
   * Meetings participate — Owner, Admin, Member. Edit meetings where the user is
   * the accountable; enforced in service layer.
   */
  ROROS_MEETINGS_PARTICIPATE: 'roros.meetings.participate',
  /** Cadence template write — Owner, Admin. Manage cadence config + template agenda. */
  ROROS_CADENCE_TEMPLATE_WRITE: 'roros.cadence.template.write',
  /**
   * Meetings manage — Owner, Admin. Full facilitation control (start, advance, pause, resume).
   * Superset of roros.meetings.write for facilitation-specific operations.
   */
  ROROS_MEETINGS_MANAGE: 'roros.meetings.manage',
  /** Outcomes read — Owner, Admin, Member. View annual outcomes list + detail modal. */
  ROROS_OUTCOMES_VIEW: 'roros.outcomes.view',
  /** Outcomes manage — Owner, Admin. Future: add/edit/archive outcomes directly. */
  ROROS_OUTCOMES_MANAGE: 'roros.outcomes.manage',

  // ─── Wave 3b Packet B — Verification Inbox ────────────────────────────────
  /** Verification Inbox view — Owner, Admin. View cascade submission inbox entries. */
  ROROS_VERIFICATION_INBOX_VIEW: 'roros.verification-inbox.view',
  /** Verification Inbox decide — Owner, Admin. Approve/reject/request-ad-hoc-review. */
  ROROS_VERIFICATION_INBOX_DECIDE: 'roros.verification-inbox.decide',

  // ─── VP Feedback Loop — In-app Notifications ──────────────────────────────
  /** Notifications view — Owner, Admin, Member. Each user sees only their own notifications. */
  ROROS_NOTIFICATIONS_VIEW: 'roros.notifications.view',

  // ─── Agent Sessions M1 ────────────────────────────────────────────────────
  /**
   * Agent sessions use — Owner, Admin, Employee.
   * Create agent sessions and send messages (agentic turns).
   * Seeded to all three roles in migration 627; renamed from roros.agent_sessions.use
   * (underscore) to roros.agent-sessions.use (hyphen) in migration 631.
   */
  ROROS_AGENT_SESSIONS_USE: 'roros.agent-sessions.use',
} as const;

/**
 * Permission type derived from the PERMISSIONS constant
 */
export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

/**
 * Permission metadata for UI display and documentation
 */
export interface PermissionMetadata {
  label: string;
  description: string;
  category: string;
  granularOf?: Permission; // If this is a granular permission of a high-level one
}

/**
 * Comprehensive metadata for all permissions
 */
export const PERMISSION_METADATA: Record<Permission, PermissionMetadata> = {
  // User Management
  [PERMISSIONS.MANAGE_USERS]: {
    label: 'Manage Users',
    description: 'Full access to invite, remove, and manage organization members',
    category: 'User Management',
  },
  [PERMISSIONS.INVITE_USERS]: {
    label: 'Invite Users',
    description: 'Send invitations to new members',
    category: 'User Management',
    granularOf: PERMISSIONS.MANAGE_USERS,
  },
  [PERMISSIONS.REMOVE_USERS]: {
    label: 'Remove Users',
    description: 'Remove members from the organization',
    category: 'User Management',
    granularOf: PERMISSIONS.MANAGE_USERS,
  },
  [PERMISSIONS.EDIT_USER_ROLES]: {
    label: 'Edit User Roles',
    description: 'Change member roles and permissions',
    category: 'User Management',
    granularOf: PERMISSIONS.MANAGE_USERS,
  },
  [PERMISSIONS.VIEW_USERS]: {
    label: 'View Users',
    description: 'View the member list and their details',
    category: 'User Management',
    granularOf: PERMISSIONS.MANAGE_USERS,
  },

  // Role Management
  [PERMISSIONS.MANAGE_ROLES]: {
    label: 'Manage Roles',
    description: 'Create, edit, and delete custom roles and permissions',
    category: 'Role Management',
  },

  // Attachments
  [PERMISSIONS.MANAGE_ATTACHMENTS]: {
    label: 'Manage Attachments',
    description: 'Full access to upload, view, and delete organization attachments',
    category: 'Organization Content',
  },

  // Organization Knowledge
  [PERMISSIONS.MANAGE_KNOWLEDGE]: {
    label: 'Manage Company Knowledge',
    description: 'Full access to upload, view, and delete company knowledge documents',
    category: 'Organization Content',
  },

  // Organization Settings
  [PERMISSIONS.MANAGE_ORGANIZATION_SETTINGS]: {
    label: 'Manage Organization Settings',
    description: 'Full access to manage organization profile, brand guidelines, and settings',
    category: 'Organization Settings',
  },

  // ── ROROS Permissions ──────────────────────────────────────────────────────
  [PERMISSIONS.ROROS_DOMAINS_VIEW]: {
    label: 'View Domains',
    description: 'View domain OMPRIKL artifacts',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_DOMAINS_MANAGE]: {
    label: 'Manage Domains',
    description: 'Activate/deactivate domains (CEO only)',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_ARTIFACTS_CREATE]: {
    label: 'Create Artifacts',
    description: 'Create OMPRIKL artifacts',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_ARTIFACTS_EDIT]: {
    label: 'Edit Artifacts',
    description: 'Edit OMPRIKL artifacts',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_ARTIFACTS_DELETE]: {
    label: 'Delete Artifacts',
    description: 'Archive/delete artifacts',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_ACTORS_MANAGE]: {
    label: 'Manage Actors',
    description: 'Manage Actor Registry',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_ADOPTION_LEVEL_CHANGE]: {
    label: 'Change Adoption Level',
    description: 'Change adoption levels (CEO only)',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_ROLE_CONTRACTS_MANAGE]: {
    label: 'Manage Role Contracts',
    description: 'Create/edit Role Contracts',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_CONTROL_TOWER_MANAGE]: {
    label: 'Manage Control Tower',
    description: 'Manage work items, WIP limits',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_CONTROL_TOWER_VIEW]: {
    label: 'View Control Tower',
    description: 'View Control Tower',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_CONTROL_TOWER_DELETE]: {
    label: 'Delete Work Items',
    description: 'Permanently delete work items (with all sub-items, history, links, and QC reports)',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_WORK_ITEMS_TRANSITION]: {
    label: 'Transition Work Items',
    description: 'Change work item status',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_DECISIONS_CREATE]: {
    label: 'Create Decisions',
    description: 'Create Decision Notes',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_DECISIONS_APPROVE]: {
    label: 'Approve Decisions',
    description: 'Approve decisions',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_QUALITY_CREATE]: {
    label: 'Create QC Reports',
    description: 'Create quality confirmation reports',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_GOVERNANCE_MANAGE]: {
    label: 'Manage Governance',
    description: 'Edit Constitution, decision rights',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_GOVERNANCE_VIEW]: {
    label: 'View Governance',
    description: 'View Constitution, decision rights, exploration zones',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_SETTINGS_MANAGE]: {
    label: 'Manage ROROS Settings',
    description: 'Manage ROROS tenant settings',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_STRUCTURE_MANAGE]: {
    label: 'Manage Structure Settings',
    description: 'Manage domains, actors, operating mode and role contracts (Owner/Admin only)',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_OVERARCHING_MANAGE]: {
    label: 'Manage Overarching Content',
    description: 'Manage company profile, values, USPs, etc.',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_OVERARCHING_VIEW]: {
    label: 'View Overarching Content',
    description: 'View overarching content',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_CADENCE_MANAGE]: {
    label: 'Manage Cadence',
    description: 'Configure cadence schedules',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_OPERATING_MODE_CHANGE]: {
    label: 'Change Operating Mode',
    description: 'Change operating mode (CEO only)',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_AGENT_SKILLS_MANAGE]: {
    label: 'Manage Agent Skills',
    description: 'Manage agent definitions and skills',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_AGENT_SKILLS_VIEW]: {
    label: 'View Agent Skills',
    description: 'View agent definitions and skills',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_OBSERVABILITY_VIEW]: {
    label: 'View Observability',
    description: 'View OS health signals, scorecards, and Mura indicators',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_BUDGET_LEDGER_VIEW]: {
    label: 'View Budget Ledger',
    description: 'View per-operator AI spend ledger (Owner + Admin)',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_DELEGATION_PROXY_CREATE]: {
    label: 'Create Proxy Delegations',
    description: 'Create delegations on behalf of another user\'s actor (Owner only)',
    category: 'ROROS',
  },
  // M-Rhythm-1: meetings + cadence template permissions
  [PERMISSIONS.ROROS_MEETINGS_READ]: {
    label: 'View Meetings',
    description: 'Read meetings and meeting details',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_MEETINGS_WRITE]: {
    label: 'Manage Meetings',
    description: 'Create, update, and skip meetings',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_MEETINGS_PARTICIPATE]: {
    label: 'Participate in Meetings',
    description: 'Join meetings as a participant',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_CADENCE_TEMPLATE_WRITE]: {
    label: 'Edit Cadence Templates',
    description: 'Edit agenda templates and defaults for cadence schedules',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_MEETINGS_MANAGE]: {
    label: 'Manage Meeting Facilitation',
    description: 'Start, pause, resume, and advance meeting facilitation (Owner/Admin)',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_OUTCOMES_VIEW]: {
    label: 'View Outcomes',
    description: 'View annual outcomes list and detail modal',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_OUTCOMES_MANAGE]: {
    label: 'Manage Outcomes',
    description: 'Add, edit, and archive outcomes directly (Owner/Admin)',
    category: 'ROROS',
  },
  // Wave 3b Packet B: Verification Inbox
  [PERMISSIONS.ROROS_VERIFICATION_INBOX_VIEW]: {
    label: 'View Verification Inbox',
    description: 'View cascade submission inbox entries (Owner/Admin)',
    category: 'ROROS',
  },
  [PERMISSIONS.ROROS_VERIFICATION_INBOX_DECIDE]: {
    label: 'Decide Verification Inbox',
    description: 'Approve, reject, or request ad-hoc review on inbox entries (Owner/Admin)',
    category: 'ROROS',
  },
  // VP Feedback Loop: In-app Notifications
  [PERMISSIONS.ROROS_NOTIFICATIONS_VIEW]: {
    label: 'View Notifications',
    description: 'View own in-app notifications (all org members including Employee role)',
    category: 'ROROS',
  },
  // Agent Sessions M1
  [PERMISSIONS.ROROS_AGENT_SESSIONS_USE]: {
    label: 'Use Agent Sessions',
    description: 'Create agent sessions and run agentic turns with AI operators (Owner/Admin/Member)',
    category: 'ROROS',
  },
};

/**
 * Owner-only permissions — explicitly excluded from the Admin grant.
 *
 * J6 fix-wave-3 B3: `roros.operating-mode.change` is a CEO-only operation per
 * PRD §1.3 / §12.3.9 — only the Owner role should hold it. The previous
 * implementation grabbed ALL permissions for Admin via `getAllPermissions()`,
 * which contradicted the explicit CEO-only intent. List any future
 * "owner-only" permissions here so both `seedDefaultRolesForOrg` and the
 * `seedDefaultRoles` script automatically keep Admin clean.
 */
export const OWNER_ONLY_PERMISSIONS: ReadonlyArray<Permission> = [
  PERMISSIONS.ROROS_OPERATING_MODE_CHANGE,
  PERMISSIONS.ROROS_DELEGATION_PROXY_CREATE,
];

/**
 * Canonical baseline granted to the system Employee role.
 *
 * Keep org-registration, default-role repair scripts, E2E fixture seeding, and
 * one-off backfill migrations in sync with this registry list. Employees can
 * read the curated ROROS surfaces and add knowledge; manager-only permissions
 * such as `roros.artifacts.edit/delete` and reprocess remain excluded.
 */
export const EMPLOYEE_SYSTEM_PERMISSIONS: ReadonlyArray<Permission> = [
  PERMISSIONS.ROROS_OVERARCHING_VIEW,
  PERMISSIONS.ROROS_OUTCOMES_VIEW,
  PERMISSIONS.ROROS_MEETINGS_READ,
  PERMISSIONS.ROROS_NOTIFICATIONS_VIEW,
  PERMISSIONS.ROROS_AGENT_SESSIONS_USE,
  PERMISSIONS.ROROS_DOMAINS_VIEW,
  PERMISSIONS.ROROS_ARTIFACTS_CREATE,
];

/**
 * Get all available permissions (used for the Owner role and for the registry
 * lookup helpers). Admin should be seeded with `getAdminPermissions()` so the
 * owner-only carve-outs above are not accidentally granted.
 *
 * @returns Array of all permission strings
 */
export function getAllPermissions(): Permission[] {
  return Object.values(PERMISSIONS);
}

/**
 * Get the permission set granted to the system Admin role.
 *
 * Identical to `getAllPermissions()` minus any permission listed in
 * `OWNER_ONLY_PERMISSIONS`. Use this anywhere you would otherwise have written
 * "give Admin everything" — keeps the Owner-vs-Admin distinction enforceable.
 */
export function getAdminPermissions(): Permission[] {
  const ownerOnly = new Set<Permission>(OWNER_ONLY_PERMISSIONS);
  return getAllPermissions().filter((p) => !ownerOnly.has(p));
}

/**
 * Get the canonical permission set granted to the system Employee role.
 */
export function getEmployeePermissions(): Permission[] {
  return [...EMPLOYEE_SYSTEM_PERMISSIONS];
}

/**
 * Get permissions grouped by category
 *
 * @returns Object with category as key and permissions array as value
 */
export function getPermissionsByCategory(): Record<string, Permission[]> {
  const grouped: Record<string, Permission[]> = {};

  for (const [permission, metadata] of Object.entries(PERMISSION_METADATA)) {
    const category = metadata.category;
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(permission as Permission);
  }

  return grouped;
}

/**
 * Check if a permission string is valid
 *
 * @param permission - Permission string to validate
 * @returns True if permission exists in registry
 */
export function isValidPermission(permission: string): permission is Permission {
  return Object.values(PERMISSIONS).includes(permission as Permission);
}
